/**
 * Decides whether a background video may download and play.
 *
 * Pure and dependency-free on purpose: every signal is injected, so the whole policy
 * is unit-testable without a DOM. `Capabilities.ts` gathers the signals and
 * `components/BackgroundVideo.ts` acts on the decision.
 *
 * A `null` field means the browser does not expose that signal, which is not the same
 * as the user opting out. Safari exposes neither the Network Information API nor
 * `deviceMemory`, so the policy must stay useful with most fields unknown - see
 * `unknown-capability-small-screen`.
 */
/**
 * `deviceMemory` is quantised to 0.25/0.5/1/2/4/8, so this admits 4GB and rejects
 * anything at or below 2GB - the class of device that runs out of memory decoding a
 * 1080p video.
 */
export const MIN_MEMORY_GB = 4;
/** Excludes roughly pre-2017 phones; current mid-range Android reports 8. */
export const MIN_CORES = 4;
export const SLOW_EFFECTIVE_TYPES = ["slow-2g", "2g", "3g"];
const PLAY = { play: true };
function block(reason) {
    return { play: false, reason };
}
/**
 * First match wins. Accessibility beats everything, a stated data preference beats a
 * measured link, a measured link beats hardware, and hardware beats screen size -
 * which is why screen size appears in exactly one rule.
 */
export function shouldPlayBackgroundVideo(env) {
    if (env.prefersReducedMotion === true)
        return block("reduced-motion");
    if (env.prefersReducedData === true)
        return block("reduced-data");
    if (env.saveData === true)
        return block("save-data");
    if (env.effectiveType !== null &&
        SLOW_EFFECTIVE_TYPES.indexOf(env.effectiveType) !== -1) {
        return block("slow-network");
    }
    if (env.deviceMemoryGB !== null && env.deviceMemoryGB < MIN_MEMORY_GB) {
        return block("low-memory");
    }
    if (env.cores !== null && env.cores < MIN_CORES) {
        return block("low-cores");
    }
    // No capability evidence whatsoever (Safari, Firefox). We cannot show that a phone
    // here is safe, and an unplayable video still costs a full download, so a small
    // screen falls back to the poster. Larger screens play and rely on the runtime
    // guards in `components/BackgroundVideo.ts` to abort if that turns out to be wrong.
    const hasNoCapabilityEvidence = env.saveData === null &&
        env.effectiveType === null &&
        env.deviceMemoryGB === null;
    if (hasNoCapabilityEvidence && env.isSmallScreen) {
        return block("unknown-capability-small-screen");
    }
    return PLAY;
}
